'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import {
  wagamourV2Surface,
  wagamourV2SurfaceBlur,
  type WagamourV2SurfaceVariant,
} from '@/lib/design/v2Surface'
import {
  SURFACE_FG_ATTR,
  surfaceForegroundStyle,
} from '@/lib/design/surfaceForeground'

export interface WagamourV2SurfaceProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  /** Canonical material tier — story | utility | modal */
  variant?: WagamourV2SurfaceVariant
  /** When true, suppress top edge highlight (rare nested cases). */
  hideEdgeHighlight?: boolean
  /** When true, suppress ambient bloom on story/modal variants. */
  hideBloom?: boolean
  /** Flex column layout for modal shells (header / scroll body / footer). */
  stacked?: boolean
  /**
   * When true, render as content-only layer inside WagamourV2Modal's clip host.
   * Shell owns border ring, radius, glass fill, and blur — see docs/V2_MODAL_DESIGN_STANDARD.md.
   * Do NOT add border, backdrop-blur, or radius here when fillShell is set.
   */
  fillShell?: boolean
}

/**
 * Canonical V2 glass surface — same material renderer as HomeV2GlassCard
 * (blur, edge highlight, warm amber body, layered depth) with cross-app variant tiers.
 *
 * Not wired into production UI yet. Home continues to use HomeV2GlassCard.
 */
export function WagamourV2Surface({
  children,
  className,
  variant = 'story',
  hideEdgeHighlight = false,
  hideBloom = false,
  stacked = false,
  fillShell = false,
  style,
  ...props
}: WagamourV2SurfaceProps) {
  const tokens = wagamourV2Surface[variant]
  const bloom = !hideBloom ? tokens.ambientBloom : null

  const showEdgeHighlight = !hideEdgeHighlight && !fillShell

  // fillShell: transparent content wrapper — material lives on WagamourV2Modal clip host
  return (
    <div
      className={cn(
        'relative',
        fillShell
          ? 'h-full min-h-0 w-full min-w-0'
          : cn('overflow-hidden border backdrop-blur-[22px]', tokens.radius),
        className
      )}
      data-surface-fg={fillShell ? undefined : SURFACE_FG_ATTR.darkGlass}
      style={{
        ...(fillShell
          ? undefined
          : {
              background: tokens.background,
              borderColor: tokens.border,
              boxShadow: tokens.shadow,
              WebkitBackdropFilter: `blur(${wagamourV2SurfaceBlur})`,
              backdropFilter: `blur(${wagamourV2SurfaceBlur})`,
              ...surfaceForegroundStyle.darkGlass,
            }),
        ...style,
      }}
      {...props}
    >
      {bloom && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-24 opacity-80"
          style={{ background: bloom }}
        />
      )}
      {showEdgeHighlight && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white/45 to-transparent"
        />
      )}
      <div
        className={cn(
          'relative',
          stacked && 'flex min-h-0 flex-1 flex-col overflow-hidden'
        )}
      >
        {children}
      </div>
    </div>
  )
}
